import type { Rect } from './layout';
import { PALETTE } from './palette';

/**
 * Character presentation seam.
 *
 * Final art does not exist yet and is out of scope. What matters is that the
 * game already knows *when* a character should react, so that dropping in
 * sprites later is an art task and not a rewrite. Nothing here touches combat
 * simulation — these states are driven entirely by playback.
 */
export type EnemyCharState =
  | 'idle'
  | 'anticipate'
  | 'attack'
  | 'drop'
  | 'block'
  | 'hit'
  | 'defeat';

export type PlayerCharState = 'idle' | 'hit' | 'blocked' | 'poisoned' | 'energized';

export interface CharAnim<T extends string> {
  state: T;
  /** performance.now() when this state began. */
  since: number;
  /** Nominal duration of the current state, in ms. */
  dur: number;
}

export function createAnim<T extends string>(state: T, dur = 400): CharAnim<T> {
  return { state, since: performance.now(), dur };
}

/** Enter a state. Re-entering the same state restarts its clock. */
export function setState<T extends string>(a: CharAnim<T>, state: T, dur = 400): void {
  a.state = state;
  a.since = performance.now();
  a.dur = dur;
}

/** 0→1 through the current state, clamped. */
export function progress(a: CharAnim<string>): number {
  if (a.dur <= 0) return 1;
  return Math.min(1, (performance.now() - a.since) / a.dur);
}

/** Ease a state back to idle once its duration has elapsed. */
export function settle<T extends string>(a: CharAnim<T>, idle: T): boolean {
  if (a.state === idle) return false;
  if (progress(a) < 1) return false;
  setState(a, idle, 0);
  return true;
}

// --- procedural stand-ins -------------------------------------------------

/**
 * A geometric mascot. Deliberately not a sprite: the seam is the state model,
 * and a shape that visibly reacts to state proves the seam works without
 * committing to any art direction.
 */
export function drawEnemyChar(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  anim: CharAnim<EnemyCharState>,
  hpRatio: number,
): void {
  const k = progress(anim);
  const cx = r.x + r.w / 2;
  const baseY = r.y + r.h * 0.92;
  const size = Math.min(r.w, r.h) * 0.62;

  // Pose offsets per state. These are the whole point: each state reads as a
  // different physical intention.
  let lean = 0;        // + leans toward the player
  let bob = 0;         // idle breathing
  let squash = 1;      // vertical scale
  let tint: string = PALETTE.danger;
  let alpha = 1;

  switch (anim.state) {
    case 'idle':
      bob = Math.sin(performance.now() / 520) * size * 0.03;
      tint = '#b0464f';
      break;
    case 'anticipate':
      lean = -size * 0.18 * Math.min(1, k * 1.6);
      squash = 1.04;
      tint = '#d0555f';
      break;
    case 'attack':
      lean = size * 0.34 * (1 - Math.pow(1 - k, 3));
      squash = 0.94;
      tint = PALETTE.danger;
      break;
    case 'drop':
      // Crouch, then release. The piece itself is drawn by the well.
      squash = 1 - 0.12 * Math.sin(k * Math.PI);
      tint = '#c98a3a';
      break;
    case 'block':
      squash = 1.06;
      tint = PALETTE.block;
      break;
    case 'hit':
      lean = -size * 0.22 * (1 - k);
      tint = '#ffffff';
      alpha = 0.7 + 0.3 * k;
      break;
    case 'defeat':
      squash = Math.max(0, 1 - k);
      alpha = Math.max(0, 1 - k);
      tint = '#5a4a55';
      break;
  }

  const h = size * squash;
  const w = size * 0.86;
  const x = cx - w / 2 + lean;
  const y = baseY - h + bob;

  ctx.save();
  ctx.globalAlpha = alpha;

  // Body: a rounded slab with a darker base so it reads as grounded.
  const body = { x, y, w, h };
  ctx.shadowColor = tint;
  ctx.shadowBlur = anim.state === 'attack' ? 18 : 8;
  fillRoundRect(ctx, body, w * 0.3, tint);
  ctx.shadowBlur = 0;

  // Eyes. Two dots that squint when attacking and vanish on defeat.
  if (anim.state !== 'defeat') {
    const eyeY = y + h * 0.34;
    const eyeR = Math.max(1.5, w * 0.07);
    const spread = w * 0.2;
    ctx.fillStyle = '#0d0b14';
    ctx.beginPath();
    ctx.arc(cx + lean - spread, eyeY, eyeR, 0, Math.PI * 2);
    ctx.arc(cx + lean + spread, eyeY, eyeR, 0, Math.PI * 2);
    ctx.fill();
  }

  // A health sliver under the body, so the character carries its own state
  // rather than relying on a bar elsewhere.
  const barW = w * 1.1;
  const barH = Math.max(3, size * 0.07);
  fillRoundRect(ctx, { x: cx - barW / 2, y: baseY + 3, w: barW, h: barH }, 2, 'rgba(0,0,0,0.45)');
  if (hpRatio > 0) {
    fillRoundRect(ctx, { x: cx - barW / 2, y: baseY + 3, w: barW * hpRatio, h: barH }, 2, PALETTE.hp);
  }

  ctx.restore();
}

/**
 * The player's presentation target. Even as a placeholder it gives enemy
 * attacks somewhere meaningful to land, instead of the HUD feeling like the
 * protagonist.
 */
export function drawPlayerChar(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  anim: CharAnim<PlayerCharState>,
  hpRatio: number,
): void {
  const k = progress(anim);
  const cx = r.x + r.w / 2;
  const baseY = r.y + r.h * 0.9;
  const size = Math.min(r.w, r.h) * 0.66;

  let lean = 0;
  let tint: string = PALETTE.frost;
  let alpha = 1;
  let squash = 1;

  switch (anim.state) {
    case 'idle':
      tint = '#3f8fb5';
      break;
    case 'hit':
      lean = -size * 0.2 * (1 - k);
      tint = '#ffffff';
      squash = 0.92 + 0.08 * k;
      break;
    case 'blocked':
      tint = PALETTE.block;
      break;
    case 'poisoned':
      tint = PALETTE.poison;
      break;
    case 'energized':
      tint = PALETTE.volt;
      break;
  }

  const h = size * squash;
  const w = size * 0.8;
  const x = cx - w / 2 + lean;
  const y = baseY - h;

  ctx.save();
  ctx.globalAlpha = alpha;
  fillRoundRect(ctx, { x, y, w, h }, w * 0.34, tint);

  // A visor slit rather than eyes: this is a figure, not a mascot.
  ctx.fillStyle = 'rgba(13,11,20,0.6)';
  fillRoundRect(ctx, { x: x + w * 0.22, y: y + h * 0.3, w: w * 0.56, h: h * 0.1 }, 2, 'rgba(13,11,20,0.6)');

  const barW = w * 1.15;
  const barH = Math.max(3, size * 0.08);
  fillRoundRect(ctx, { x: cx - barW / 2, y: baseY + 3, w: barW, h: barH }, 2, 'rgba(0,0,0,0.45)');
  if (hpRatio > 0) {
    fillRoundRect(ctx, { x: cx - barW / 2, y: baseY + 3, w: barW * hpRatio, h: barH }, 2, PALETTE.hp);
  }
  ctx.restore();
}

function fillRoundRect(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  radius: number,
  fill: string,
): void {
  const k = Math.min(radius, r.w / 2, r.h / 2);
  ctx.beginPath();
  ctx.moveTo(r.x + k, r.y);
  ctx.arcTo(r.x + r.w, r.y, r.x + r.w, r.y + r.h, k);
  ctx.arcTo(r.x + r.w, r.y + r.h, r.x, r.y + r.h, k);
  ctx.arcTo(r.x, r.y + r.h, r.x, r.y, k);
  ctx.arcTo(r.x, r.y, r.x + r.w, r.y, k);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
}
